"use client";

import { useEffect, useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  EVERY_DAY,
  readStored,
  removeStored,
  storageAvailable,
  WEEKDAYS_ONLY,
  WeekdayToggles,
  weekdaySet,
  writeStored,
} from "@/components/tools/study/controls";
import { ProgressLogTable } from "@/components/tools/study/progress-log";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type DayNumber,
  dayFormatter,
  isoFromDay,
  localToday,
  parseIsoDay,
} from "@/lib/tool-kit/study/dates";
import {
  type LogEntry,
  logWithChanges,
  parseSavedWritingGoal,
  planWriting,
  restoreWritingGoal,
  type SavedWritingGoal,
  upsertLogEntry,
  writingDays,
} from "@/lib/tool-kit/study/writing-goal";

const STORAGE_KEY = "citationeasy-writing-goal-v1";
const MAX_DAYS = 1100;

/** English strings. A translated content file overrides these through `ui`. */
export const WRITING_GOAL_CALCULATOR_STRINGS = {
  presetsLabel: "Presets",
  presetNano: "NaNoWriMo: 50,000 words in 30 days",
  presetThesis: "Thesis chapter: 10,000 words in 6 weeks",
  targetLabel: "Target word count",
  targetPlaceholder: "e.g. 8000",
  writtenLabel: "Words written so far",
  startLabel: "Start counting from",
  deadlineLabel: "Deadline",
  writingDaysLabel: "Writing days",
  weekdaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  results: "Your Daily Target",
  perDay: "Words per writing day",
  writingDays: "Writing days",
  remaining: "Words to go",
  perWeek: "Words per full week",
  progress: "{percent}% written: {written} of {target} words",
  done: "Target reached: {written} of {target} words. Nothing left to schedule.",
  scheduleHeading: "Day-by-day targets",
  colDate: "Date",
  colToday: "Write today",
  colTotal: "Total by end of day",
  copySchedule: "Copy schedule",
  copied: "Copied!",
  scheduleLine: "{date}: write {words} (total {total})",
  logHeading: "Progress log",
  logIntro:
    'Each time you write, update "Words written so far" with your document\'s word count and log it. The log shows how much you added each time.',
  logToday: "Log today's total",
  colLogDate: "Date",
  colLogTotal: "Word count",
  colLogChange: "Added",
  colLogRemove: "Remove",
  removeEntry: "Remove the entry for {date}",
  clearLog: "Clear log",
  logEmpty: "No entries yet.",
  storageNote:
    "Your goal and log are saved in this browser's local storage, so they're here when you come back on this device. Nothing is uploaded.",
  storageUnavailable:
    "This browser isn't letting the page save data (private browsing or blocked storage), so the goal and log will clear when you close the page.",
  forget: "Forget saved data",
  enterTarget: "Enter a target word count and a deadline.",
  deadlineBeforeStart: "The deadline is before the start date.",
  noWritingDays:
    "No writing days fall between those dates. Tick more days or move the deadline.",
  tooLong: "Plans run for up to {max} days. Pick an earlier deadline.",
} as const;

interface Goal {
  target: string;
  written: string;
  start: string;
  deadline: string;
  days: boolean[];
  log: LogEntry[];
}

const EMPTY_GOAL: Goal = {
  target: "",
  written: "0",
  start: "",
  deadline: "",
  days: [...EVERY_DAY],
  log: [],
};

/** A fresh goal running from today, 30 days inclusive. */
function freshGoal(today: DayNumber): Goal {
  return {
    ...EMPTY_GOAL,
    start: isoFromDay(today),
    deadline: isoFromDay(today + 29),
  };
}

function loadGoal(today: DayNumber): Goal {
  const raw = readStored(STORAGE_KEY);
  let saved: SavedWritingGoal | null = null;
  try {
    saved = raw ? parseSavedWritingGoal(JSON.parse(raw)) : null;
  } catch {
    saved = null;
  }
  if (!saved) {
    return freshGoal(today);
  }
  const restored = restoreWritingGoal(saved, today);
  return {
    target: restored.target,
    written: restored.written,
    start: restored.start,
    deadline: restored.deadline,
    days: EVERY_DAY.map((_, index) => restored.weekdays.includes(index)),
    log: restored.log,
  };
}

export default function WritingGoalCalculatorWidget() {
  const strings = useToolUi(WRITING_GOAL_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [goal, setGoal] = useState<Goal>(EMPTY_GOAL);
  const [today, setToday] = useState<DayNumber | null>(null);
  const [canSave, setCanSave] = useState(true);

  useEffect(() => {
    const now = localToday();
    setToday(now);
    setGoal(loadGoal(now));
    setCanSave(storageAvailable());
  }, []);

  useEffect(() => {
    if (today === null || !canSave) {
      return;
    }
    const timer = setTimeout(() => {
      const saved: SavedWritingGoal = {
        target: goal.target,
        written: goal.written,
        start: goal.start,
        deadline: goal.deadline,
        weekdays: goal.days.flatMap((on, index) => (on ? [index] : [])),
        log: goal.log,
      };
      writeStored(STORAGE_KEY, JSON.stringify(saved));
    }, 300);
    return () => clearTimeout(timer);
  }, [goal, today, canSave]);

  const update = (patch: Partial<Goal>) =>
    setGoal((current) => ({ ...current, ...patch }));
  const checked = useMemo(
    () => writingDays(goal, weekdaySet(goal.days), MAX_DAYS),
    [goal]
  );
  const problemText =
    checked.problem === "tooLong"
      ? t(strings.tooLong, { max: MAX_DAYS })
      : checked.problem && strings[checked.problem];
  const isError = checked.problem !== null && checked.problem !== "enterTarget";
  const target = parseIntegerInput(goal.target) ?? 0;
  const written = Math.max(0, parseIntegerInput(goal.written) ?? 0);
  const plan = useMemo(
    () => planWriting(target, written, checked.days),
    [target, written, checked.days]
  );
  const formatDay = useMemo(
    () =>
      dayFormatter(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    [locale]
  );
  const daysPerWeek = goal.days.filter(Boolean).length;
  const percent = target > 0 ? Math.min(100, (written / target) * 100) : 0;

  function applyPreset(words: number, span: number, days: readonly boolean[]) {
    const start = parseIsoDay(goal.start) ?? today ?? localToday();
    update({
      target: String(words),
      written: "0",
      start: isoFromDay(start),
      deadline: isoFromDay(start + span),
      days: [...days],
    });
  }

  function forget() {
    removeStored(STORAGE_KEY);
    setGoal(freshGoal(today ?? localToday()));
  }

  const scheduleText = plan.rows
    .map((row) =>
      t(strings.scheduleLine, {
        date: formatDay(row.day),
        words: fmt(row.words),
        total: fmt(row.total),
      })
    )
    .join("\n");
  const logRows = logWithChanges(goal.log);

  return (
    <>
      <section className="space-y-4">
        <div className="space-y-2">
          <p className={HINT}>{strings.presetsLabel}</p>
          <div className="flex flex-wrap gap-3">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => applyPreset(50_000, 29, EVERY_DAY)}
              type="button"
            >
              {strings.presetNano}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => applyPreset(10_000, 41, WEEKDAYS_ONLY)}
              type="button"
            >
              {strings.presetThesis}
            </button>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="wg-target"
            label={strings.targetLabel}
            numeric
            onChange={(value) => update({ target: value })}
            placeholder={strings.targetPlaceholder}
            value={goal.target}
          />
          <TextField
            id="wg-written"
            label={strings.writtenLabel}
            numeric
            onChange={(value) => update({ written: value })}
            value={goal.written}
          />
          <TextField
            id="wg-start"
            label={strings.startLabel}
            onChange={(value) => update({ start: value })}
            type="date"
            value={goal.start}
          />
          <TextField
            id="wg-deadline"
            label={strings.deadlineLabel}
            onChange={(value) => update({ deadline: value })}
            type="date"
            value={goal.deadline}
          />
        </div>
        <WeekdayToggles
          labels={strings.weekdaysShort}
          legend={strings.writingDaysLabel}
          name="wg-day"
          onChange={(days) => update({ days })}
          value={goal.days}
        />
      </section>

      <section aria-live="polite" className="space-y-5">
        {problemText && isError ? (
          <ErrorNotice>{problemText}</ErrorNotice>
        ) : null}
        {problemText && !isError ? <Notice>{problemText}</Notice> : null}
        {checked.problem === null ? (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.perDay}
                value={fmt(plan.perDay)}
              />
              <StatCard
                label={strings.writingDays}
                value={fmt(checked.days.length)}
              />
              <StatCard label={strings.remaining} value={fmt(plan.remaining)} />
              <StatCard
                label={strings.perWeek}
                value={fmt(plan.perDay * daysPerWeek)}
              />
            </StatGrid>
            <div className="space-y-2">
              <div
                aria-hidden="true"
                className="h-3 w-full overflow-hidden rounded-full bg-muted"
              >
                <div
                  className="h-full bg-primary"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="text-muted-foreground text-sm">
                {t(strings.progress, {
                  percent: fmt(percent, { maximumFractionDigits: 1 }),
                  written: fmt(written),
                  target: fmt(target),
                })}
              </p>
            </div>
            {plan.rows.length === 0 ? (
              <Notice>
                {t(strings.done, {
                  written: fmt(written),
                  target: fmt(target),
                })}
              </Notice>
            ) : (
              <div className="space-y-3">
                <h3 className={WIDGET_H3}>{strings.scheduleHeading}</h3>
                <div className="max-h-[28rem] overflow-auto rounded-lg border border-border bg-card">
                  <table className={TABLE}>
                    <thead className="sticky top-0 bg-card">
                      <tr>
                        {[
                          strings.colDate,
                          strings.colToday,
                          strings.colTotal,
                        ].map((label) => (
                          <th
                            className={`${TH} whitespace-nowrap`}
                            key={label}
                            scope="col"
                          >
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {plan.rows.map((row) => (
                        <tr key={row.day}>
                          <td className={`${TD} whitespace-nowrap`}>
                            {formatDay(row.day)}
                          </td>
                          <td className={TD}>{fmt(row.words)}</td>
                          <td className={TD}>{fmt(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copySchedule}
                  text={scheduleText}
                />
              </div>
            )}
          </>
        ) : null}
      </section>

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.logHeading}</h3>
        <p className="text-muted-foreground text-sm">{strings.logIntro}</p>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={
              today === null || parseIntegerInput(goal.written) === null
            }
            onClick={() => {
              if (today !== null) {
                update({
                  log: upsertLogEntry(goal.log, isoFromDay(today), written),
                });
              }
            }}
            type="button"
          >
            {strings.logToday}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={goal.log.length === 0}
            onClick={() => update({ log: [] })}
            type="button"
          >
            {strings.clearLog}
          </button>
        </div>
        {logRows.length === 0 ? (
          <p className={HINT}>{strings.logEmpty}</p>
        ) : (
          <ProgressLogTable
            fmt={fmt}
            formatDay={formatDay}
            labels={{
              date: strings.colLogDate,
              total: strings.colLogTotal,
              change: strings.colLogChange,
              remove: strings.colLogRemove,
              removeEntry: strings.removeEntry,
            }}
            onRemove={(date) =>
              update({ log: goal.log.filter((entry) => entry.date !== date) })
            }
            rows={logRows}
          />
        )}
        <p className={HINT}>
          {canSave ? strings.storageNote : strings.storageUnavailable}
        </p>
        <button className={BUTTON_SMALL} onClick={forget} type="button">
          {strings.forget}
        </button>
      </section>
    </>
  );
}
